// Phase 15 Plan 08 Task 2 — Edit-name modal (own profile only).
// Phase 17 Plan 06 Task 1 (cleanup-reskin) — token + Btn primitive re-skin.
// ARCA-82 WS2 — migrated to <Dialog> primitive (scrim/panel/focus-trap live there).

import { type JSX, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ApiError } from '../../net/client'
import { changeDisplayName } from '../../net/profile'
import { invalidateProfileCache } from '../../stores/profileCache'
import { Btn } from '../components/Btn'
import { Dialog } from '../components/Dialog'

export interface EditNameModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly currentDisplayName: string
  readonly selfUserId: string
}

function mapErrorCodeToMessageId(code: string | undefined): string {
  switch (code) {
    case 'DISPLAY_NAME_TOO_LONG':
      return 'social.error.display_name_too_long'
    case 'DISPLAY_NAME_PROFANE':
      return 'social.error.display_name_profane'
    case 'DISPLAY_NAME_TOO_RECENT':
      return 'social.error.display_name_too_recent'
    default:
      return 'error.server_error.body'
  }
}

function mapErrorCodeToDefault(code: string | undefined): string {
  switch (code) {
    case 'DISPLAY_NAME_TOO_LONG':
      return 'That name is too long. Try 24 characters or fewer.'
    case 'DISPLAY_NAME_PROFANE':
      return 'That name is not allowed. Try another.'
    case 'DISPLAY_NAME_TOO_RECENT':
      return 'You changed your name recently. Try again later.'
    default:
      return 'The runes are slow today. Try again in a moment.'
  }
}

function extractErrorCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined
  if (typeof err.body === 'object' && err.body !== null) {
    const body = err.body as { code?: unknown; errors?: unknown }
    if (typeof body.code === 'string') return body.code
    if (Array.isArray(body.errors) && body.errors.length > 0) {
      const first = body.errors[0] as { code?: unknown }
      if (typeof first.code === 'string') return first.code
    }
  }
  return undefined
}

export function EditNameModal(props: EditNameModalProps): JSX.Element | null {
  const { open, onClose, currentDisplayName, selfUserId } = props
  const intl = useIntl()

  const [value, setValue] = useState(currentDisplayName)
  const [submitting, setSubmitting] = useState(false)
  const [errorId, setErrorId] = useState<string | null>(null)
  const [errorDefault, setErrorDefault] = useState<string>('')

  if (!open) return null

  const trimmed = value.trim()
  const canSubmit =
    !submitting &&
    trimmed.length >= 1 &&
    trimmed.length <= 24 &&
    trimmed !== currentDisplayName

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return
    setSubmitting(true)
    setErrorId(null)
    try {
      await changeDisplayName(trimmed)
      invalidateProfileCache(selfUserId)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('runegambit:toast', {
            detail: { id: 'social.profile.name_changed' },
          }),
        )
        window.dispatchEvent(
          new CustomEvent('runegambit:profile-name-changed', {
            detail: { user_uuid: selfUserId, display_name: trimmed },
          }),
        )
      }
      onClose()
    } catch (err: unknown) {
      const code = extractErrorCode(err)
      setErrorId(mapErrorCodeToMessageId(code))
      setErrorDefault(mapErrorCodeToDefault(code))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    // Center-dialog flavor (UI-SPEC §5): scrim + panel + focus trap + dismissal
    // live in <Dialog>/ModalScrim. padding:0 so the header band bleeds to the
    // rounded panel edge (overflow:hidden clips it).
    <Dialog
      onClose={onClose}
      testId="edit-name-modal"
      labelledBy="edit-name-modal-title"
      dismissOnScrimTap={!submitting}
      panelStyle={{ padding: 0, overflow: 'hidden' }}
    >
      <div
        style={{
          height: '48px',
          backgroundColor: 'var(--color-paper-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom:
            '1px solid color-mix(in srgb, var(--color-surface-stroke) 30%, transparent)',
        }}
      >
        <h2
          id="edit-name-modal-title"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-18)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="social.profile.edit_name.title"
            defaultMessage="Edit your name"
          />
        </h2>
      </div>
      <div style={{ padding: 'var(--space-5)' }}>
        <p
          style={{
            margin: '0 0 var(--space-4)',
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            lineHeight: 1.5,
            color: 'var(--color-text-secondary)',
          }}
        >
          <FormattedMessage
            id="social.profile.edit_name.body"
            defaultMessage="Pick a name. Up to 24 characters."
          />
        </p>
        <input
          data-testid="edit-name-input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoCapitalize="words"
          spellCheck={true}
          maxLength={24}
          disabled={submitting}
          aria-label={intl.formatMessage({
            id: 'social.profile.edit_name.input_aria',
            defaultMessage: 'Display name',
          })}
          style={{
            width: '100%',
            height: '48px',
            padding: '0 var(--space-4)',
            borderRadius: 'var(--radius-pill)',
            border: 'none',
            boxShadow:
              'inset 0 0 0 2px color-mix(in srgb, var(--color-surface-stroke) 55%, transparent)',
            backgroundColor: 'var(--color-card)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-family-body)',
            fontWeight: 600,
            fontSize: 'var(--font-size-16)',
            boxSizing: 'border-box',
          }}
        />
        {errorId !== null && (
          <p
            role="alert"
            data-testid="edit-name-error"
            style={{
              marginTop: 'var(--space-3)',
              marginBottom: 0,
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-13)',
              color: 'var(--color-cta-destructive)',
            }}
          >
            <FormattedMessage id={errorId} defaultMessage={errorDefault} />
          </p>
        )}
        <div
          style={{
            marginTop: 'var(--space-5)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Btn
            kind="primary"
            size="lg"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            testId="edit-name-submit"
          >
            <FormattedMessage
              id="social.profile.edit_name.save_cta"
              defaultMessage="Update name"
            />
          </Btn>
        </div>
      </div>
    </Dialog>
  )
}
